import './styles/main.scss';

// ============================================================================
// GOOGLE DESIGN SECRET ANIMATIONS, PIXEL 10 PRO & DUNONG AI CONTROLLER
// ============================================================================

interface ArticleData {
  title: string;
  desc: string;
  tags: string[];
  element: HTMLElement;
}

// Global View Switcher for Pixel 10 Pro Phone Screen
(window as any).switchDunongView = (view: 'home' | 'chat', autoFocus = false) => {
  const homeView = document.getElementById('dunong-view-home');
  const chatView = document.getElementById('dunong-view-chat');

  if (view === 'home') {
    homeView?.classList.add('active');
    chatView?.classList.remove('active');
  } else {
    chatView?.classList.add('active');
    homeView?.classList.remove('active');
    if (autoFocus) {
      setTimeout(() => {
        document.getElementById('dunong-prompt-input')?.focus({ preventScroll: true });
      }, 150);
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  initCustomCursor();
  initMagneticCapsule();
  initScrollAnimations();
  initCardSpotlightAndTilt();
  initSearchModal();
  initMobileDrawer();
  initDunongAIApp();
  initSignupButton();
  initGraphicsViewer();
  initAdSensePush();
});

// ============================================================================
// 1. SOLID WHITE CURSOR DOT
// ============================================================================
function initCustomCursor() {
  const follower = document.getElementById('gd-cursor-follower');

  if (!follower) return;

  let mouseX = window.innerWidth / 2;
  let mouseY = window.innerHeight / 2;
  let currentX = mouseX;
  let currentY = mouseY;
  let isActive = false;

  const LERP_FACTOR = 0.2;

  window.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;

    if (!isActive) {
      isActive = true;
      follower.classList.add('active');
    }
  });

  window.addEventListener('mouseleave', () => {
    isActive = false;
    follower.classList.remove('active');
  });

  function renderCursor() {
    if (!follower) return;

    currentX += (mouseX - currentX) * LERP_FACTOR;
    currentY += (mouseY - currentY) * LERP_FACTOR;

    follower.style.transform = `translate3d(${currentX}px, ${currentY}px, 0) translate(-50%, -50%)`;

    requestAnimationFrame(renderCursor);
  }
  requestAnimationFrame(renderCursor);

  const targets = document.querySelectorAll('.js-cursor-target');

  targets.forEach((target) => {
    target.addEventListener('mouseenter', (e) => {
      const el = e.currentTarget as HTMLElement;
      const linkStyle = el.getAttribute('data-link-style');

      follower.classList.remove('hover-default', 'hover-chip');

      if (linkStyle === 'chip') {
        follower.classList.add('hover-chip');
      } else {
        follower.classList.add('hover-default');
      }
    });

    target.addEventListener('mouseleave', () => {
      follower.classList.remove('hover-default', 'hover-chip');
    });
  });
}

// ============================================================================
// 2. INTERACTIVE DUNONG AI APP ON PIXEL 10 PRO
// ============================================================================
function initDunongAIApp() {
  const promptInput = document.getElementById('dunong-prompt-input') as HTMLInputElement | null;
  const sendBtn = document.getElementById('dunong-send-btn');
  const chatMessagesContainer = document.getElementById('dunong-chat-messages');
  const aiChatCard = document.getElementById('dunong-card-aichat');

  if (!promptInput || !sendBtn || !chatMessagesContainer) return;

  let autoDemoTimer: any = null;
  let isDeviceInViewport = true;

  // Viewport Observer to ensure auto demo stops immediately when scrolling away to bottom
  const deviceContainer = document.querySelector('.pixel-device-container');
  if (deviceContainer && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        isDeviceInViewport = entry.isIntersecting;
        if (!isDeviceInViewport) {
          clearTimeout(autoDemoTimer);
        } else {
          runAutoDemo();
        }
      });
    }, { threshold: 0.1 });
    observer.observe(deviceContainer);
  }

  const resetChatState = () => {
    chatMessagesContainer.innerHTML = '<div class="chat-placeholder-text" id="chat-placeholder">what\'s the move?</div>';
    promptInput.value = '';
    sendBtn.classList.remove('active');
  };

  const renderSampleWebsiteResponse = (container: HTMLElement) => {
    const aiResponseWrapper = document.createElement('div');
    aiResponseWrapper.className = 'chat-ai-response-container';
    aiResponseWrapper.innerHTML = `
      <h3 class="ai-response-title">Clean & Modern Sample Website Template</h3>
      <p class="ai-response-desc">Here is a complete, single-file HTML and CSS template for a responsive landing page. It is fully self-contained, meaning you can copy this code, save it on your computer, and open it instantly in any browser.</p>
      
      <div class="ai-code-block">
        <div class="code-header">
          <span class="code-lang-label">html</span>
          <button class="code-copy-btn js-cursor-target" data-link-style="chip" title="Copy Code">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="12" height="12" rx="2"/>
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h8a2 2 0 012 2v1"/>
            </svg>
          </button>
        </div>
        <div class="code-content"><span class="tag">&lt;!DOCTYPE html&gt;</span>
<span class="tag">&lt;html</span> <span class="attr">lang</span>=<span class="str">"en"</span><span class="tag">&gt;</span>
<span class="tag">&lt;head&gt;</span>
  <span class="tag">&lt;meta</span> <span class="attr">charset</span>=<span class="str">"UTF-8"</span><span class="tag">&gt;</span>
  <span class="tag">&lt;meta</span> <span class="attr">name</span>=<span class="str">"viewport"</span> <span class="attr">content</span>=<span class="str">"width=device-width, initial-scale=1.0"</span><span class="tag">&gt;</span>
  <span class="tag">&lt;title&gt;</span>Sample Landing Page<span class="tag">&lt;/title&gt;</span>
  <span class="tag">&lt;style&gt;</span>
    <span class="comment">/* Code Style Variables */</span>
    <span class="attr">:root</span> {
      <span class="attr">--primary</span>: <span class="str">#0070f3</span>;
    }
  <span class="tag">&lt;/style&gt;</span>
<span class="tag">&lt;/head&gt;</span>
<span class="tag">&lt;body&gt;</span>
  <span class="tag">&lt;main&gt;</span>Welcome to GiG Studio<span class="tag">&lt;/main&gt;</span>
<span class="tag">&lt;/body&gt;</span>
<span class="tag">&lt;/html&gt;</span></div>
        <button class="code-floating-down-btn js-cursor-target" data-link-style="chip" title="Scroll down code">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 4v14M19 12l-7 7-7-7" stroke="#000000" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;

    container.appendChild(aiResponseWrapper);
    
    // Smooth auto scroll to show generated title and top of code block
    setTimeout(() => {
      container.scrollTop = container.scrollHeight;
    }, 100);

    // Attach copy event
    const copyBtn = aiResponseWrapper.querySelector('.code-copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(`<!DOCTYPE html>\n<html lang="en">\n<head>\n  <meta charset="UTF-8">\n  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n  <title>Sample Landing Page</title>\n</head>\n<body>\n</body>\n</html>`);
        copyBtn.innerHTML = '✓ Copied';
        setTimeout(() => {
          copyBtn.innerHTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h8a2 2 0 012 2v1"/></svg>`;
        }, 2000);
      });
    }
  };

  const handleSendPrompt = async () => {
    const text = promptInput.value.trim();
    if (!text) return;

    promptInput.value = '';
    sendBtn.classList.remove('active');

    // Remove placeholder text
    const placeholder = chatMessagesContainer.querySelector('.chat-placeholder-text');
    if (placeholder) {
      placeholder.remove();
    }

    // Append User Message Pill
    const userMsg = document.createElement('div');
    userMsg.className = 'chat-user-msg';
    userMsg.textContent = text;
    chatMessagesContainer.appendChild(userMsg);
    chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight;

    // Append 3-Dot Waving AI Thinking Indicator on Left Side
    const thinkingBubble = document.createElement('div');
    thinkingBubble.className = 'chat-ai-thinking-bubble';
    thinkingBubble.innerHTML = `
      <span class="thinking-label">Dunong thinking</span>
      <div class="typing-dots">
        <span class="dot"></span>
        <span class="dot"></span>
        <span class="dot"></span>
      </div>
    `;
    chatMessagesContainer.appendChild(thinkingBubble);
    chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight;

    // Simulate thinking duration with 3-dot waving animation before delivering AI response
    setTimeout(() => {
      thinkingBubble.remove();

      if (text.toLowerCase().includes('sample website') || text.toLowerCase().includes('website') || text.toLowerCase().includes('create')) {
        renderSampleWebsiteResponse(chatMessagesContainer);
      } else {
        // General Response Bubble
        const aiMsg = document.createElement('div');
        aiMsg.className = 'chat-user-msg';
        aiMsg.style.alignSelf = 'flex-start';
        aiMsg.style.backgroundColor = '#f4f6f9';
        aiMsg.textContent = `Dunong AI generated response for: "${text}"`;
        chatMessagesContainer.appendChild(aiMsg);
        chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight;
      }
    }, 1200);
  };

  sendBtn.addEventListener('click', handleSendPrompt);
  promptInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      handleSendPrompt();
    }
  });

  promptInput.addEventListener('input', () => {
    if (promptInput.value.trim().length > 0) {
      sendBtn.classList.add('active');
    } else {
      sendBtn.classList.remove('active');
    }
  });

  // ==========================================================================
  // FAST AUTOMATED INFINITE LOOPING ANIMATION SEQUENCE
  // (Only runs when virtual device is in viewport - never loops when scrolled down!)
  // ==========================================================================
  const runAutoDemo = () => {
    clearTimeout(autoDemoTimer);

    if (!isDeviceInViewport) return;

    (window as any).switchDunongView('home');
    resetChatState();

    // Step 1: Display Get Started home screen for 1.2s, then pulse AI Chat card
    autoDemoTimer = setTimeout(() => {
      if (!isDeviceInViewport) return;
      aiChatCard?.classList.add('active-pulse');

      // Step 2: Open AI Chat screen (showing "what's the move?")
      autoDemoTimer = setTimeout(() => {
        if (!isDeviceInViewport) return;
        aiChatCard?.classList.remove('active-pulse');
        (window as any).switchDunongView('chat');

        // Step 3: Fast auto-type prompt "Create sample website" into input fill
        autoDemoTimer = setTimeout(() => {
          if (!isDeviceInViewport) return;
          const demoPrompt = "Create sample website";
          let charIdx = 0;

          const typeChar = () => {
            if (!isDeviceInViewport) return;
            if (charIdx <= demoPrompt.length) {
              promptInput.value = demoPrompt.substring(0, charIdx);
              promptInput.dispatchEvent(new Event('input'));
              charIdx++;
              autoDemoTimer = setTimeout(typeChar, 25);
            } else {
              // Step 4: Auto-submit prompt & trigger 3-dot waving AI thinking animation
              autoDemoTimer = setTimeout(() => {
                if (!isDeviceInViewport) return;
                handleSendPrompt();

                // Step 5: Showcase generated code for 3.8s (giving time for 3-dot animation + code reading), then LOOP!
                autoDemoTimer = setTimeout(() => {
                  if (isDeviceInViewport) {
                    runAutoDemo();
                  }
                }, 4000);

              }, 250);
            }
          };
          typeChar();

        }, 600);

      }, 800);

    }, 1200);
  };

  // Start fast auto demo loop on page load if visible
  if (isDeviceInViewport) {
    runAutoDemo();
  }
}

// ============================================================================
// 3. SECRET MAGNETIC CAPSULE & ICON PHYSICS
// ============================================================================
function initMagneticCapsule() {
  const chipContainer = document.querySelector('.Navigation_chip_container') as HTMLElement | null;
  const chipBtns = document.querySelectorAll('.Navigation_btn_chip');

  if (!chipContainer) return;

  chipContainer.addEventListener('mousemove', (e: MouseEvent) => {
    const rect = chipContainer.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const pullX = (e.clientX - centerX) * 0.25;
    const pullY = (e.clientY - centerY) * 0.25;

    chipContainer.style.transform = `translate3d(${pullX.toFixed(2)}px, ${pullY.toFixed(2)}px, 0)`;
  });

  chipContainer.addEventListener('mouseleave', () => {
    chipContainer.style.transform = 'translate3d(0, 0, 0)';
  });

  chipBtns.forEach((btn) => {
    const btnEl = btn as HTMLElement;

    btnEl.addEventListener('mousemove', (e: MouseEvent) => {
      const rect = btnEl.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const pullX = (e.clientX - centerX) * 0.35;
      const pullY = (e.clientY - centerY) * 0.35;

      btnEl.style.transform = `translate3d(${pullX.toFixed(2)}px, ${pullY.toFixed(2)}px, 0)`;
    });

    btnEl.addEventListener('mouseleave', () => {
      btnEl.style.transform = 'translate3d(0, 0, 0)';
    });
  });
}

// ============================================================================
// 4. SCROLL REVEAL PARALLAX ANIMATIONS
// ============================================================================
function initScrollAnimations() {
  const animatedElements = document.querySelectorAll('.js-target');

  if (!('IntersectionObserver' in window)) {
    animatedElements.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          const yStart = target.getAttribute('data-y-start') || '40';
          target.style.setProperty('--y-start', `${yStart}px`);
          target.classList.add('is-visible');
          observer.unobserve(target);
        }
      });
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px'
    }
  );

  animatedElements.forEach((el) => observer.observe(el));
}

// ============================================================================
// 5. CARD SPOTLIGHT & TILT
// ============================================================================
function initCardSpotlightAndTilt() {
  const cards = document.querySelectorAll('.card-interactive');

  cards.forEach((card) => {
    const cardEl = card as HTMLElement;

    cardEl.addEventListener('mousemove', (e: MouseEvent) => {
      const rect = cardEl.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = ((y - centerY) / centerY) * -3;
      const rotateY = ((x - centerX) / centerX) * 3;

      cardEl.style.transform = `perspective(1000px) rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg) scale3d(1.015, 1.015, 1.015)`;
    });

    cardEl.addEventListener('mouseleave', () => {
      cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    });
  });
}

// ============================================================================
// 6. SEARCH OVERLAY MODAL DRAWER LOGIC
// ============================================================================
function initSearchModal() {
  const searchTriggerBtn = document.getElementById('search-trigger-btn');
  const searchCloseBtn = document.getElementById('search-close-btn');
  const searchModal = document.getElementById('search-modal');
  const searchBackdrop = document.getElementById('search-backdrop');
  const searchInput = document.getElementById('gd-search-input') as HTMLInputElement | null;
  const searchResultsContainer = document.getElementById('gd-search-results');
  const searchTagsContainer = document.getElementById('gd-search-tags');

  if (!searchModal || !searchTriggerBtn) return;

  const openModal = () => {
    searchModal.classList.add('open');
    searchModal.setAttribute('aria-hidden', 'false');
    setTimeout(() => {
      searchInput?.focus();
    }, 100);
    renderSearchResults('');
  };

  const closeModal = () => {
    searchModal.classList.remove('open');
    searchModal.setAttribute('aria-hidden', 'true');
    if (searchInput) searchInput.value = '';
  };

  searchTriggerBtn.addEventListener('click', openModal);
  searchCloseBtn?.addEventListener('click', closeModal);
  searchBackdrop?.addEventListener('click', closeModal);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && searchModal.classList.contains('open')) {
      closeModal();
    }
  });

  const articles: ArticleData[] = Array.from(document.querySelectorAll('.FeatureBlock_feature, .Hero_link')).map((el) => {
    const titleEl = el.querySelector('.Heading_title');
    const descEl = el.querySelector('.TitleBlock_title__description p');
    const tagsAttr = el.getAttribute('data-tags') || '';
    const tags = tagsAttr.split(',').map(t => t.trim()).filter(Boolean);

    return {
      title: titleEl?.textContent?.trim() || '',
      desc: descEl?.textContent?.trim() || '',
      tags: tags,
      element: el as HTMLElement
    };
  });

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.toLowerCase().trim();
    renderSearchResults(query);
  });

  if (searchTagsContainer) {
    const tagButtons = searchTagsContainer.querySelectorAll('.chip-tag');
    tagButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const category = btn.getAttribute('data-category');
        if (btn.classList.contains('active')) {
          btn.classList.remove('active');
          renderSearchResults(searchInput?.value || '');
        } else {
          tagButtons.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          if (category) renderFilteredByCategory(category);
        }
      });
    });
  }

  function renderSearchResults(query: string) {
    if (!searchResultsContainer) return;
    searchResultsContainer.innerHTML = '';

    const filtered = articles.filter(art => {
      if (!query) return true;
      return art.title.toLowerCase().includes(query) || 
             art.desc.toLowerCase().includes(query) ||
             art.tags.some(t => t.toLowerCase().includes(query));
    });

    if (filtered.length === 0) {
      searchResultsContainer.innerHTML = `
        <div style="padding: 24px; color: var(--theme-foreground-muted); font-size: 15px;">
          No projects found matching "${query}".
        </div>
      `;
      return;
    }

    filtered.forEach(art => {
      const item = document.createElement('div');
      item.className = 'search-result-item js-cursor-target';
      item.setAttribute('data-link-style', 'default');
      item.style.padding = '16px 0';
      item.style.borderBottom = '1px solid rgba(255,255,255,0.08)';
      item.style.cursor = 'pointer';
      item.innerHTML = `
        <div style="font-family: var(--font-global-heading); font-size: 18px; font-weight: 600; color: #ffffff;">${art.title}</div>
        <div style="font-size: 14px; color: var(--theme-foreground-muted); margin-top: 4px;">${art.desc}</div>
      `;
      item.addEventListener('click', () => {
        closeModal();
        art.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      searchResultsContainer.appendChild(item);
    });
  }

  function renderFilteredByCategory(category: string) {
    if (!searchResultsContainer) return;
    searchResultsContainer.innerHTML = '';

    const filtered = articles.filter(art => art.tags.includes(category));

    filtered.forEach(art => {
      const item = document.createElement('div');
      item.className = 'search-result-item js-cursor-target';
      item.setAttribute('data-link-style', 'default');
      item.style.padding = '16px 0';
      item.style.borderBottom = '1px solid rgba(255,255,255,0.08)';
      item.style.cursor = 'pointer';
      item.innerHTML = `
        <div style="font-family: var(--font-global-heading); font-size: 18px; font-weight: 600; color: #ffffff;">${art.title}</div>
        <div style="font-size: 14px; color: var(--theme-foreground-muted); margin-top: 4px;">${art.desc}</div>
      `;
      item.addEventListener('click', () => {
        closeModal();
        art.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      searchResultsContainer.appendChild(item);
    });
  }
}

// ============================================================================
// 7. MOBILE DRAWER NAVIGATION
// ============================================================================
function initMobileDrawer() {
  const menuTriggerBtn = document.getElementById('menu-trigger-btn');
  const mobileMenuClose = document.getElementById('mobile-menu-close');
  const mobileMenu = document.getElementById('mobile-menu');

  if (!mobileMenu || !menuTriggerBtn) return;

  const toggleDrawer = () => {
    const isOpen = mobileMenu.classList.contains('open');
    if (isOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  };

  const openDrawer = () => {
    mobileMenu.classList.add('open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    menuTriggerBtn.classList.add('active');
  };

  const closeDrawer = () => {
    mobileMenu.classList.remove('open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    menuTriggerBtn.classList.remove('active');
  };

  menuTriggerBtn.addEventListener('click', toggleDrawer);
  mobileMenuClose?.addEventListener('click', closeDrawer);

  const drawerLinks = mobileMenu.querySelectorAll('.gd-drawer-link');
  drawerLinks.forEach(link => {
    link.addEventListener('click', closeDrawer);
  });
}

// ============================================================================
// 8. GOOGLE MATERIAL 3 SIGN UP BUTTON HANDLER
// ============================================================================
function initSignupButton() {
  const signupBtn = document.getElementById('m3-signup-btn');
  if (!signupBtn) return;

  signupBtn.addEventListener('click', () => {
    const icon = signupBtn.querySelector('.m3-btn-icon');
    const text = signupBtn.querySelector('.m3-btn-text');

    if (icon && text) {
      icon.textContent = 'check_circle';
      text.textContent = 'Subscribed!';
      signupBtn.classList.add('submitted');

      setTimeout(() => {
        icon.textContent = 'mail';
        text.textContent = 'Sign up';
        signupBtn.classList.remove('submitted');
      }, 3500);
    }
  });
}

// ============================================================================
// 9. PROFESSIONAL GRAPHICS VIEWER & LIGHTBOX CONTROLLER
// ============================================================================
interface GalleryItem {
  src: string;
  title: string;
  caption: string;
  meta?: string;
}

function initGraphicsViewer() {
  const cards = document.querySelectorAll<HTMLElement>('.graphics-card');
  if (cards.length === 0) return;

  const galleryItems: GalleryItem[] = Array.from(cards).map(card => ({
    src: card.getAttribute('data-img-src') || card.querySelector('img')?.src || '',
    title: card.getAttribute('data-img-title') || card.querySelector('.card-title')?.textContent?.trim() || 'Graphic Artwork',
    caption: card.getAttribute('data-img-caption') || card.querySelector('.card-caption')?.textContent?.trim() || '',
    meta: card.getAttribute('data-img-meta') || ''
  }));

  let modal = document.getElementById('graphics-viewer-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'graphics-viewer-modal';
    modal.className = 'graphics-viewer-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="viewer-topbar">
        <div class="viewer-title-group">
          <span class="viewer-count-chip" id="viewer-count-chip">1 / 1</span>
          <h3 class="viewer-image-title" id="viewer-image-title">Graphic Artwork</h3>
        </div>
        <div class="viewer-controls">
          <button class="ctrl-btn" id="viewer-zoom-in" title="Zoom In (+)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </button>
          <button class="ctrl-btn" id="viewer-zoom-out" title="Zoom Out (-)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </button>
          <button class="ctrl-btn" id="viewer-zoom-reset" title="Reset Zoom (1:1)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
          </button>
          <button class="ctrl-btn close-btn" id="viewer-close" title="Close (Esc)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
      <div class="viewer-canvas" id="viewer-canvas">
        <button class="nav-arrow arrow-prev" id="viewer-prev" title="Previous Image (←)">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div class="viewer-image-wrapper" id="viewer-img-wrapper">
          <img id="viewer-active-img" src="" alt="Full Resolution Graphic Artwork" />
        </div>
        <button class="nav-arrow arrow-next" id="viewer-next" title="Next Image (→)">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
      <div class="viewer-bottombar">
        <p class="viewer-caption" id="viewer-caption-text"></p>
        <div class="filmstrip-container" id="viewer-filmstrip"></div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  let currentIndex = 0;
  let zoomLevel = 1;
  let panX = 0;
  let panY = 0;
  let isDragging = false;
  let startX = 0;
  let startY = 0;

  const countChip = document.getElementById('viewer-count-chip')!;
  const titleEl = document.getElementById('viewer-image-title')!;
  const captionEl = document.getElementById('viewer-caption-text')!;
  const activeImg = document.getElementById('viewer-active-img') as HTMLImageElement;
  const imgWrapper = document.getElementById('viewer-img-wrapper')!;
  const filmstrip = document.getElementById('viewer-filmstrip')!;
  const canvas = document.getElementById('viewer-canvas')!;

  function renderFilmstrip() {
    filmstrip.innerHTML = '';
    galleryItems.forEach((item, idx) => {
      const thumb = document.createElement('div');
      thumb.className = `filmstrip-thumb ${idx === currentIndex ? 'active' : ''}`;
      thumb.innerHTML = `<img src="${item.src}" alt="${item.title}" />`;
      thumb.addEventListener('click', (e) => {
        e.stopPropagation();
        goToImage(idx);
      });
      filmstrip.appendChild(thumb);
    });
  }

  function updateTransform() {
    imgWrapper.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${zoomLevel})`;
  }

  function resetZoomPan() {
    zoomLevel = 1;
    panX = 0;
    panY = 0;
    updateTransform();
  }

  function goToImage(idx: number) {
    if (idx < 0) idx = galleryItems.length - 1;
    if (idx >= galleryItems.length) idx = 0;

    currentIndex = idx;
    const item = galleryItems[currentIndex];

    activeImg.src = item.src;
    titleEl.textContent = item.title;
    captionEl.textContent = item.caption || item.meta || '';
    countChip.textContent = `Image ${currentIndex + 1} of ${galleryItems.length}`;

    resetZoomPan();
    renderFilmstrip();
  }

  function openViewer(startIndex: number) {
    goToImage(startIndex);
    modal?.classList.add('active');
    modal?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeViewer() {
    modal?.classList.remove('active');
    modal?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  cards.forEach((card, idx) => {
    card.addEventListener('click', () => openViewer(idx));
  });

  document.getElementById('viewer-close')?.addEventListener('click', closeViewer);
  document.getElementById('viewer-prev')?.addEventListener('click', () => goToImage(currentIndex - 1));
  document.getElementById('viewer-next')?.addEventListener('click', () => goToImage(currentIndex + 1));

  document.getElementById('viewer-zoom-in')?.addEventListener('click', () => {
    zoomLevel = Math.min(zoomLevel + 0.35, 3.5);
    updateTransform();
  });

  document.getElementById('viewer-zoom-out')?.addEventListener('click', () => {
    zoomLevel = Math.max(zoomLevel - 0.35, 0.7);
    updateTransform();
  });

  document.getElementById('viewer-zoom-reset')?.addEventListener('click', resetZoomPan);

  canvas.addEventListener('mousedown', (e) => {
    if (zoomLevel <= 1) return;
    isDragging = true;
    startX = e.clientX - panX;
    startY = e.clientY - panY;
  });

  window.addEventListener('mousemove', (e) => {
    if (!isDragging) return;
    panX = e.clientX - startX;
    panY = e.clientY - startY;
    updateTransform();
  });

  window.addEventListener('mouseup', () => {
    isDragging = false;
  });

  canvas.addEventListener('wheel', (e) => {
    if (!modal?.classList.contains('active')) return;
    e.preventDefault();
    if (e.deltaY < 0) {
      zoomLevel = Math.min(zoomLevel + 0.2, 3.5);
    } else {
      zoomLevel = Math.max(zoomLevel - 0.2, 0.8);
    }
    updateTransform();
  }, { passive: false });

  window.addEventListener('keydown', (e) => {
    if (!modal?.classList.contains('active')) return;
    if (e.key === 'Escape') closeViewer();
    if (e.key === 'ArrowLeft') goToImage(currentIndex - 1);
    if (e.key === 'ArrowRight') goToImage(currentIndex + 1);
  });
}

// ============================================================================
// 10. GOOGLE ADSENSE PUSH INITIALIZER
// ============================================================================
function initAdSensePush() {
  try {
    const adUnits = document.querySelectorAll('.adsbygoogle');
    adUnits.forEach(() => {
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    });
  } catch (err) {
    console.debug('AdSense init notice:', err);
  }
}

